<script lang="ts">
    import { mdiArrowLeft, mdiTagEditOutline } from "@mdi/js"
    import Icon from "../../../widgets/Icon.svelte"
    import { _ } from "svelte-i18n"
    import { langField, location } from "../../../../i18n"
    import CustomerRatingsLine from "../preview/CustomerRatingsLine.svelte"
    import ProductAvailabilityStatus from "../widgets/ProductAvailabilityStatus.svelte"
    import ProductOptions from "../widgets/ProductOptions.svelte"
    import PriceTag from "../widgets/PriceTag.svelte"
    import Action from "./Action.svelte"
    import SectionReferences from "./SectionReferences.svelte"
    import LasersheetInput from "../widgets/lasersheet/LasersheetInput.svelte"
    import ProductKeyNumberRangeForm from "./ProductKeyNumberRangeForm.svelte"
    import ProductEngravingForm from "./ProductEngravingForm.svelte"
    import { obj2str } from "../../../../../../api/hooks/lib/ssr"
    import { deepCopy } from "../../../../utils"
    import { lasersheetEngraving, overlay } from "../../../../store"
    import { storeLasersheetEngravingToLocalStorage } from "../widgets/helper"
    import { login } from "../../../../functionTools/login"
    import { isEuCountry } from "../../../../europe"

    let {
        product,
        setActiveImage,
        headingTag = "h1",
    }: {
        product: Product
        setActiveImage: (idx: number) => void
        headingTag?: "h1" | "div"
    } = $props()

    let isOnSale = $derived(!$login && product.price_net > product.price_net_end_customer)

    let user_inputs: CartUserInputs = $state({})
    const engravingVisible =
        product.user_inputs?.engraving_line_length_1 ||
        product.user_inputs?.engraving_line_length_2 ||
        product.user_inputs?.engraving_line_length_3

    const productKeyFormVisible = product.user_inputs?.key_number_range_from || product.user_inputs?.key_number_range_to
    const productionCountryCode = $derived((product.production_country || "").trim().toUpperCase())
    const showMadeInEuBadge = $derived(
        !productionCountryCode || productionCountryCode === "EU" || isEuCountry(productionCountryCode)
    )
    const showCustomProductionCountryBadge = $derived(
        !!productionCountryCode && productionCountryCode !== "EU" && isEuCountry(productionCountryCode)
    )
    $effect(() => {
        if (!user_inputs) user_inputs = {}
        if (obj2str($lasersheetEngraving) == obj2str(user_inputs.lasersheet_engraving)) return
        user_inputs = { ...user_inputs, lasersheet_engraving: deepCopy($lasersheetEngraving) }
        storeLasersheetEngravingToLocalStorage(user_inputs)
    })
</script>

<section class="product-details">
    <header class="product-header">
        {#if $location?.push || $location?.pop}
            <nav class="breadcrumb">
                <a
                    href="/"
                    class="back-button"
                    onclick={(e) => {
                        e.preventDefault()
                        history.back()
                    }}
                    aria-label="Back to overview"
                >
                    <Icon path={mdiArrowLeft} />
                    {$_("product.back-to-overview")}
                </a>
            </nav>
        {/if}
        {#if isOnSale}
            <div class="sale-badge">
                <span class="icon">%</span>
                <span class="text">{$_("product.sale-badge")}</span>
            </div>
        {/if}
        <svelte:element this={headingTag} class="product-name">{langField(product.name, "")}</svelte:element>
    </header>

    <article class="details">
        <div class="product-info">
            <p class="item-number">
                {$_("product.item-number-short")}: {product.base_code}
                {#if product.gtin}
                    <span class="separator"> | </span>
                    {$_("product.gtin")}: {product.gtin}
                {/if}
            </p>

            <CustomerRatingsLine {product} />

            <div class="availability-status">
                <ProductAvailabilityStatus {product} />
            </div>
        </div>
    </article>

    <div class="product-seals" role="complementary" aria-label="Produkt-Zertifikate und Auszeichnungen">
        <a
            href="https://www.n-tv.de/ratgeber/Life-Living-Award-2024-Das-sind-Deutschlands-beliebteste-Marken-article24796258.html"
            target="_blank"
            rel="nofollow"
            aria-label="Learn more about the Life & Living Award 2024"
        >
            <img src="media/beliebter-anbieter.svg" alt="Beliebter Anbieter 2024" />
        </a>
        {#if showMadeInEuBadge}
            <img src="media/made-in-eu.svg" alt="Made in EU" />
        {:else if showCustomProductionCountryBadge}
            <div class="made-in-country-badge" aria-label={`Made in ${productionCountryCode}`}>
                Made in {productionCountryCode}
            </div>
        {/if}
    </div>
    <section>
        <ProductOptions {product} {setActiveImage} />
    </section>
    {#if productKeyFormVisible}
        <section>
            <ProductKeyNumberRangeForm {product} bind:user_inputs />
        </section>
    {/if}
    {#if engravingVisible}
        <section>
            <ProductEngravingForm {product} bind:user_inputs />
        </section>
    {/if}
    {#if product.lasersheet_configurator?.length}
        <section class="lasersheet-input">
            <LasersheetInput {product} {setActiveImage} />
        </section>
    {/if}
    <section>
        <PriceTag {product} />
    </section>
    <section>
        <Action {product} bind:userInputs={user_inputs} />
    </section>
    <section>
        <SectionReferences {product} />
    </section>
</section>

<style lang="less">
    @import "../../../../css/variables.less";

    .product-details {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 1.2rem;
        header {
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
            h1 {
                margin: 0px;
                padding: 0px;
            }
            .sale-badge {
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                color: var(--red-100, #c4102d);
                font-size: 0.9rem;
                font-weight: 500;
                .icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 1.4rem;
                    height: 1.4rem;
                    background-color: var(--red-100, #c4102d);
                    border-radius: 50%;
                    color: white;
                    font-size: 0.8rem;
                    font-weight: 600;
                }
                .text {
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                }
            }
            .breadcrumb {
                .back-button {
                    display: flex;
                    gap: 0.3rem;
                    align-items: center;
                    font-size: 1.2rem;
                    color: var(--grey-400);
                    font-size: var(--bodyfontsize);
                    text-decoration: none;
                    &:hover {
                        text-decoration: underline;
                    }
                }
            }
        }

        @media @max-mobile-product {
            .lasersheet-input {
                display: none;
            }
        }
        .product-seals {
            display: inline-flex;
            flex-flow: wrap;
            gap: 20px;
            justify-content: flex-end;
            margin-top: -48px;
            margin-bottom: 0;
            a {
                display: flex;
                align-items: center;
            }

            img {
                max-width: 80px;
                max-height: 50px;
                width: 100px;
            }

            .made-in-country-badge {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border: 1px solid var(--grey-300);
                border-radius: 6px;
                padding: 0.35rem 0.6rem;
                font-size: 0.75rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.03em;
                min-height: 42px;
                color: var(--grey-700);
                background: white;
            }

            @media @max-mobile-product {
                margin-top: 0;
                gap: 12px;
                justify-content: flex-start;
            }
        }
        .details {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-wrap: wrap;
            gap: 2.4rem;

            @media @max-mobile-product {
                gap: 1.2rem;
            }

            .product-info {
                .item-number {
                    font-size: var(--bodyfontsize);
                    padding: 0px;
                    margin: 0px;
                }

                display: flex;
                flex-direction: column;
                gap: 0.6rem;
            }
        }
    }
</style>
